import { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Snackbar from "@mui/material/Snackbar";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ScienceIcon from "@mui/icons-material/Science";
import { api } from "../api/client";
import type { DataSourceRow, MarketRow, Meta, SignalRow } from "../api/types";
import { useAuth } from "../auth/AuthProvider";
import { useMutation, useResource } from "../hooks/useResource";
import {
  ConfirmDialog,
  EmptyState,
  ErrorBanner,
  JsonField,
  LoadingState,
  Mono,
  PageHeader,
  RoleGate,
  ago,
} from "../components/common";
import { MONO } from "../theme";

/**
 * Signal administration.
 *
 * The dry-run button is the important control here. A filter is a JSON tree
 * compiled to SQL, and the difference between "matches 4,000 rows" and "matches 0"
 * is invisible from reading it — so the console offers the count before the signal
 * is switched on and starts spending enrichment budget.
 */

const EXAMPLE_FILTER = `{
  "and": [
    { "field": "class", "op": "eq", "value": "C" },
    { "field": "violationstatus", "op": "eq", "value": "Open" }
  ]
}`;

interface Draft {
  key: string;
  sourceId: string;
  filterText: string;
  filterValid: boolean;
  window: string;
  dedupKey: string;
  minOccurrences: string;
  minOccurrenceSpan: string;
  entityResolver: string;
  marketId: string;
  kind: string;
  label: string;
  whyNowText: string;
  whyNowValid: boolean;
  active: boolean;
}

const emptyDraft = (): Draft => ({
  key: "",
  sourceId: "",
  filterText: EXAMPLE_FILTER,
  filterValid: true,
  window: "P30D",
  dedupKey: "",
  minOccurrences: "1",
  minOccurrenceSpan: "",
  entityResolver: "",
  marketId: "",
  kind: "event",
  label: "",
  whyNowText: "[]",
  whyNowValid: true,
  active: false,
});

export function SignalsPage() {
  const { can } = useAuth();
  const signals = useResource<{ items: SignalRow[] }>("/api/signals");
  const sources = useResource<{ items: DataSourceRow[] }>("/api/sources");
  const markets = useResource<{ items: MarketRow[] }>("/api/markets");
  const meta = useResource<Meta>("/api/meta");

  const [editing, setEditing] = useState<SignalRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<SignalRow | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const toggle = useMutation(async (signal: SignalRow) => {
    await api.put(`/api/signals/${signal.id}`, { active: !signal.active });
  });
  const remove = useMutation(async (signal: SignalRow) => {
    await api.del(`/api/signals/${signal.id}?cascade=true`);
  });

  if (signals.loading && !signals.data) return <LoadingState label="Loading signals…" />;

  return (
    <Box>
      <PageHeader
        title="Signals"
        subtitle="Rules over a source's records. Each firing becomes a candidate lead."
        actions={
          <RoleGate allowed={can("operator")} needs="operator">
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)}>
              Add signal
            </Button>
          </RoleGate>
        }
      />

      <ErrorBanner error={signals.error} onRetry={signals.reload} />
      <ErrorBanner error={toggle.error ?? remove.error} />

      {signals.data && signals.data.items.length === 0 ? (
        <EmptyState title="No signals configured" hint="Add one against a data source." />
      ) : null}

      {signals.data && signals.data.items.length > 0 ? (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Key</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Market</TableCell>
                <TableCell>Kind</TableCell>
                <TableCell>Window</TableCell>
                <TableCell>Resolver</TableCell>
                <TableCell align="right">Events</TableCell>
                <TableCell>Last fired</TableCell>
                <TableCell>Active</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {signals.data.items.map((signal) => (
                <TableRow key={signal.id} hover>
                  <TableCell>
                    <Mono>{signal.key}</Mono>
                    {signal.label ? (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {signal.label}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <Mono>{signal.sourceKey}</Mono>
                  </TableCell>
                  <TableCell>
                    {signal.marketKey ?? (
                      <Tooltip title="A signal with no market never produces a lead — assemble skips it.">
                        <Chip label="unrouted" color="warning" variant="outlined" />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={signal.kind}
                      variant="outlined"
                      color={signal.kind === "event" ? "primary" : "default"}
                    />
                  </TableCell>
                  <TableCell>
                    <Mono>{signal.window}</Mono>
                    {signal.minOccurrences > 1 ? (
                      <Typography variant="caption" color="text.secondary" display="block">
                        ≥{signal.minOccurrences} occurrences
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>{signal.entityResolver ?? "—"}</TableCell>
                  <TableCell align="right">{signal.events}</TableCell>
                  <TableCell>{ago(signal.lastFiredAt)}</TableCell>
                  <TableCell>
                    <RoleGate allowed={can("operator")} needs="operator">
                      <Switch
                        checked={signal.active}
                        size="small"
                        onChange={() => void toggle.run(signal).then((ok) => ok && signals.reload())}
                      />
                    </RoleGate>
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <RoleGate allowed={can("operator")} needs="operator">
                        <IconButton onClick={() => setEditing(signal)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </RoleGate>
                      <RoleGate allowed={can("owner")} needs="owner">
                        <IconButton color="error" onClick={() => setDeleting(signal)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </RoleGate>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      ) : null}

      {creating || editing ? (
        <SignalDialog
          signal={editing}
          sources={sources.data?.items ?? []}
          markets={markets.data?.items ?? []}
          meta={meta.data}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={(message) => {
            setCreating(false);
            setEditing(null);
            setToast(message);
            signals.reload();
          }}
        />
      ) : null}

      <ConfirmDialog
        open={deleting !== null}
        title={`Delete ${deleting?.key}?`}
        confirmLabel="Delete signal and its events"
        pending={remove.pending}
        body={
          <>
            This discards {deleting?.events ?? 0} recorded firing(s) — the history of everything this signal
            ever produced. Switching it off keeps the history.
          </>
        }
        onCancel={() => setDeleting(null)}
        onConfirm={() => {
          if (!deleting) return;
          void remove.run(deleting).then((ok) => {
            if (!ok) return;
            setDeleting(null);
            setToast(`Deleted ${deleting.key}`);
            signals.reload();
          });
        }}
      />

      <Snackbar open={toast !== null} autoHideDuration={4000} onClose={() => setToast(null)} message={toast ?? ""} />
    </Box>
  );
}

function SignalDialog({
  signal,
  sources,
  markets,
  meta,
  onClose,
  onSaved,
}: {
  signal: SignalRow | null;
  sources: DataSourceRow[];
  markets: MarketRow[];
  meta: Meta | undefined;
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const [draft, setDraft] = useState<Draft>(emptyDraft());
  const [preview, setPreview] = useState<{ matches: number; samples: unknown[] } | null>(null);

  useEffect(() => {
    if (!signal) {
      setDraft(emptyDraft());
      return;
    }
    setDraft({
      key: signal.key,
      sourceId: signal.sourceId,
      filterText: JSON.stringify(signal.filter, null, 2),
      filterValid: true,
      window: signal.window,
      dedupKey: signal.dedupKey.join(", "),
      minOccurrences: String(signal.minOccurrences),
      minOccurrenceSpan: signal.minOccurrenceSpan ?? "",
      entityResolver: signal.entityResolver ?? "",
      marketId: signal.marketId ?? "",
      kind: signal.kind,
      label: signal.label ?? "",
      whyNowText: JSON.stringify(signal.whyNow ?? [], null, 2),
      whyNowValid: true,
      active: signal.active,
    });
  }, [signal]);

  const body = () => ({
    key: draft.key.trim(),
    sourceId: draft.sourceId,
    filter: JSON.parse(draft.filterText),
    window: draft.window.trim() || "P30D",
    dedupKey: draft.dedupKey
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s !== ""),
    minOccurrences: Number(draft.minOccurrences) || 1,
    minOccurrenceSpan: draft.minOccurrenceSpan.trim() === "" ? null : draft.minOccurrenceSpan.trim(),
    entityResolver: draft.entityResolver === "" ? null : draft.entityResolver,
    marketId: draft.marketId === "" ? null : draft.marketId,
    kind: draft.kind,
    label: draft.label.trim() === "" ? null : draft.label.trim(),
    whyNow: JSON.parse(draft.whyNowText) as unknown,
    active: draft.active,
  });

  const save = useMutation(async () => {
    if (signal) await api.put(`/api/signals/${signal.id}`, body());
    else await api.post("/api/signals", body());
  });

  const dryRun = useMutation(async () => {
    const result = await api.post<{ matches: number; samples: unknown[] }>("/api/signals/preview", {
      sourceId: draft.sourceId,
      filter: JSON.parse(draft.filterText),
      limit: 3,
    });
    setPreview(result);
  });

  const submittable =
    draft.key.trim() !== "" &&
    draft.sourceId !== "" &&
    draft.filterValid &&
    draft.whyNowValid &&
    draft.dedupKey.trim() !== "" &&
    !save.pending;

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{signal ? `Edit ${signal.key}` : "Add signal"}</DialogTitle>
      <DialogContent>
        <ErrorBanner error={save.error ?? dryRun.error} />
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Key"
              value={draft.key}
              onChange={(e) => setDraft({ ...draft, key: e.target.value })}
              fullWidth
            />
            <TextField
              select
              label="Source"
              value={draft.sourceId}
              onChange={(e) => setDraft({ ...draft, sourceId: e.target.value })}
              fullWidth
            >
              {sources.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.key}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField
              select
              label="Market"
              value={draft.marketId}
              onChange={(e) => setDraft({ ...draft, marketId: e.target.value })}
              helperText="Without a market this signal fires but never becomes a lead."
              fullWidth
            >
              <MenuItem value="">No market</MenuItem>
              {markets.map((m) => (
                <MenuItem key={m.id} value={m.id}>
                  {m.key}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Entity resolver"
              value={draft.entityResolver}
              onChange={(e) => setDraft({ ...draft, entityResolver: e.target.value })}
              helperText="Turns a firing into a property + owner."
              fullWidth
            >
              <MenuItem value="">None</MenuItem>
              {(meta?.entityResolvers ?? []).map((r) => (
                <MenuItem key={r} value={r}>
                  {r}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField
              select
              label="Kind"
              value={draft.kind}
              onChange={(e) => setDraft({ ...draft, kind: e.target.value })}
              helperText="event = dated news with a deadline; stock = a standing condition."
              sx={{ minWidth: 200 }}
            >
              {(meta?.signalKinds ?? ["event", "stock"]).map((k) => (
                <MenuItem key={k} value={k}>
                  {k}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Label"
              value={draft.label}
              onChange={(e) => setDraft({ ...draft, label: e.target.value })}
              helperText="Short human name on a lead card, e.g. “Roof tear-off”."
              fullWidth
            />
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField
              label="Window"
              value={draft.window}
              onChange={(e) => setDraft({ ...draft, window: e.target.value })}
              helperText="ISO duration, e.g. P30D"
              sx={{ width: 140 }}
            />
            <TextField
              label="Dedup key"
              value={draft.dedupKey}
              onChange={(e) => setDraft({ ...draft, dedupKey: e.target.value })}
              helperText="Comma-separated payload paths identifying one subject, e.g. bbl"
              fullWidth
            />
            <TextField
              label="Min occurrences"
              value={draft.minOccurrences}
              onChange={(e) => setDraft({ ...draft, minOccurrences: e.target.value.replace(/\D/g, "") })}
              sx={{ width: 150 }}
            />
            <TextField
              label="Min span"
              value={draft.minOccurrenceSpan}
              onChange={(e) => setDraft({ ...draft, minOccurrenceSpan: e.target.value })}
              helperText="Optional, e.g. P7D"
              sx={{ width: 140 }}
            />
          </Stack>

          <JsonField
            label="Filter"
            value={draft.filterText}
            rows={10}
            onChange={(filterText, filterValid) => setDraft({ ...draft, filterText, filterValid })}
            helperText={`Ops: ${(meta?.filterOps ?? []).join(", ")}. Compiled to SQL over the source payload on save.`}
          />

          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              startIcon={<ScienceIcon />}
              onClick={() => void dryRun.run(undefined)}
              disabled={draft.sourceId === "" || !draft.filterValid || dryRun.pending}
            >
              {dryRun.pending ? "Matching…" : "Dry run against stored rows"}
            </Button>
            {preview ? (
              <Typography variant="body2" color={preview.matches === 0 ? "warning.main" : "success.main"}>
                {preview.matches.toLocaleString("en-US")} matching record(s)
              </Typography>
            ) : null}
          </Stack>

          {preview && preview.samples.length > 0 ? (
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 1.5,
                bgcolor: "background.default",
                borderRadius: 1,
                fontFamily: MONO,
                fontSize: 11,
                maxHeight: 200,
                overflow: "auto",
              }}
            >
              {JSON.stringify(preview.samples, null, 2)}
            </Box>
          ) : null}

          <JsonField
            label="Why-now fragments"
            value={draft.whyNowText}
            rows={6}
            onChange={(whyNowText, whyNowValid) => setDraft({ ...draft, whyNowText, whyNowValid })}
            helperText='Array of { "text": "...", "requires": ["units"] }. A fragment whose facts are missing is dropped rather than rendered with holes.'
          />

          {draft.active ? (
            <Alert severity="warning">
              An active signal fires on the next pipeline run, which spends metered enrichment quota.
            </Alert>
          ) : null}

          <Stack direction="row" spacing={1} alignItems="center">
            <Switch checked={draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} />
            <Typography variant="body2">Active</Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={save.pending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!submittable}
          onClick={() =>
            void save
              .run(undefined)
              .then((ok) => ok && onSaved(signal ? `Saved ${draft.key}` : `Created ${draft.key}`))
          }
        >
          {save.pending ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
